import { formatFileNameFromArpMedia, MAX_SAMPLES_PER_PACK } from '@arpeggi/shared'
import { Box, Button, Icons, Spinner } from '@arpeggi/ui'
import { saveAs } from 'file-saver'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { requiresUserLoggedIn } from '../../../__legacy__/components/AuthFlow/AuthFlowModal'
import { Modal, ModalComponents } from '../../../__legacy__/components/core/Modal'
import { LoadingStatus } from '../../../__legacy__/components/studio/activitybar/sample-packs/SamplePackDropdown'
import { Instructions } from '../../../__legacy__/components/studio/mint/register-song-modal/components/Instructions'
import { useArpMedias } from '../../../__legacy__/queries/arpMedia'
import { CRITERIA_SP_ID } from '../hooks'
import { useFilesZipDownload } from '../hooks/useFilesZipDownload'
import { Actions } from '../primitives/Actions'

export const CRITERIA_ZIP_LINK =
  'https://arpeggi-prod-public.s3.us-west-2.amazonaws.com/sample-pack-zips/Criteria+%E2%80%94+Official+Sample+Pack.zip'

interface Props {
  samplePackId: number
  zipFileName: string
  open: boolean
  onOpenChange: (value: boolean) => void
  isControlled?: boolean
}

export const DownloadSoundsModal: FC<Props> = observer(
  ({ samplePackId, zipFileName, open, onOpenChange, isControlled }) => {
    const soundsQuery = useArpMedias(
      { perPage: MAX_SAMPLES_PER_PACK, samplePackId },
      { enabled: open }
    )
    const { loadingStatus, zipAndDownloadFilesFromUrls } = useFilesZipDownload()
    const wrappedTrigger = requiresUserLoggedIn(
      <Actions.SecondaryAction
        id="download-modal"
        variant={'secondary'}
        prefixIcon={<Icons.Download height="16px" width="16px" />}
      >
        Download Samples
      </Actions.SecondaryAction>
    )

    const handleDownload = () => {
      // Special case to download the zip for the criteria pack
      if (samplePackId === CRITERIA_SP_ID) {
        saveAs(CRITERIA_ZIP_LINK, zipFileName)
        return
      }

      const urls = soundsQuery.data?.pages.flatMap((page) =>
        page.data
          .map((sound) => ({
            fileName: formatFileNameFromArpMedia(sound),
            url: sound.cachedWavDataUri
          }))
          .filter((x) => !!x.url)
      )

      window.analytics.track('Button Clicked', {
        button: 'download',
        file: zipFileName
      })

      zipAndDownloadFilesFromUrls({ urls, zipFileName })
      onOpenChange(false)
    }

    return (
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        trigger={isControlled ? undefined : wrappedTrigger}
        contentCss={{ width: '744px', padding: '32px' }}
      >
        <ModalComponents.HeaderContainer>
          <ModalComponents.Title>Use these sounds in any DAW!</ModalComponents.Title>
          <ModalComponents.Subtitle>
            Download these sounds, create with them using your favorite DAW, and upload your
            finished track. By crediting the sounds you use, your music will be highlighted for new
            fans to discover.
          </ModalComponents.Subtitle>
        </ModalComponents.HeaderContainer>
        <ModalComponents.Container>
          {soundsQuery.isLoading && (
            <Box column justifyCenter alignCenter css={{ minHeight: '300px' }}>
              <Spinner css={{ width: '24px', height: '24px', strokeWidth: '1.5px' }} />
            </Box>
          )}
          {!soundsQuery.isLoading && (
            <Instructions
              instructions={[
                {
                  title: 'Step 1: Download Arpeggi sounds',
                  description:
                    'Each sample in the .zip file has an important ID in the file name. Organize these samples however you’d like, but be sure not to change the file name.'
                },
                {
                  title: 'Step 2: Import sounds into Live, FL Studio, or any other DAW',
                  description:
                    'Simply use the sounds in your DAW like you would use any other sample. Your DAW project file will include references to the sounds you import.'
                },
                {
                  title: 'Step 3: Upload your track and showcase the sounds you used',
                  description:
                    'When you’re finished, upload a .wav and your project file. Arpeggi will scan your project file for references to any Arpeggi sounds and automatically credit them.'
                }
              ]}
            />
          )}
        </ModalComponents.Container>
        <ModalComponents.FooterContainer end>
          <Button
            disabled={soundsQuery.isLoading}
            css={{ backgroundColor: '#7266F9', '&:hover': { opacity: 0.8 } }}
            loading={loadingStatus === LoadingStatus.LOADING}
            onClick={handleDownload}
          >
            Continue to download
          </Button>
        </ModalComponents.FooterContainer>
      </Modal>
    )
  }
)
